import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Building2, Ban, Play, Plus, Settings2, Copy, Download, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { buildWelcomePdf } from "../../lib/welcomePdf";

type Tenant = {
  id: string; name: string; slug: string; status: string;
  applied_template?: string | null; applied_template_version?: string | null;
  default_timezone: string;
};
type TenantUser = {
  id: string; name: string; email: string; active: boolean;
  role?: string | null; must_change_password?: boolean; last_login_at?: string | null;
};
type Welcome = {
  tenant: { id: string; name: string; slug: string };
  owner: { id: string; name: string; email: string; role: string };
  temporary_password: string; must_change_password: boolean; display_once: boolean;
  credential_issuance?: { id: string; version_number: number };
};

export const TENANT_TIMEZONES = [
  { value: "America/Vancouver", label: "Pacific Time — Vancouver, BC" },
  { value: "America/Edmonton", label: "Mountain Time — Edmonton, AB" },
  { value: "Asia/Manila", label: "Philippine Time — Manila" },
  { value: "America/Toronto", label: "Eastern Time — Toronto, ON" },
  { value: "America/Winnipeg", label: "Central Time — Winnipeg, MB" },
  { value: "America/Halifax", label: "Atlantic Time — Halifax, NS" },
  { value: "America/St_Johns", label: "Newfoundland Time — St. John's, NL" },
  { value: "UTC", label: "Coordinated Universal Time" },
] as const;

function TimezoneSelect({ value, onChange, id, testId }: {
  value: string; onChange: (value: string) => void; id: string; testId: string;
}) {
  const listId = `${id}-options`;
  return (
    <>
      <Input id={id} list={listId} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search city or timezone…" data-testid={testId} autoComplete="off" />
      <datalist id={listId}>
        {TENANT_TIMEZONES.map((zone) => (
          <option key={zone.value} value={zone.value}>{zone.label}</option>
        ))}
      </datalist>
      <p className="text-xs text-slate-500">
        Uses daylight-saving rules automatically. Choose an IANA timezone, not PST/MST.
      </p>
    </>
  );
}

export function welcomeText(result: Welcome) {
  return [
    "RemediKeep — Welcome Login", "",
    `Tenant: ${result.tenant.name}`,
    `Login URL: ${window.location.origin}/login`,
    `Email: ${result.owner.email}`,
    `Temporary Password: ${result.temporary_password}`, "",
    "You will be required to create a new password after your first successful login.",
    "Do not share these credentials.",
  ].join("\n");
}

function TenantFunctions({ tenant, onChanged }: { tenant: Tenant; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [allowAdditional, setAllowAdditional] = useState(false);
  const [welcome, setWelcome] = useState<Welcome | null>(null);
  const [timezone, setTimezone] = useState(tenant.default_timezone || "UTC");

  useEffect(() => { setTimezone(tenant.default_timezone || "UTC"); }, [tenant.default_timezone]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/tenants/${tenant.id}/users`);
      setUsers(data);
    } catch (e) { toastError(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && !welcome) loadUsers();
  }, [open, welcome]);

  const saveTimezone = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${tenant.id}`, { default_timezone: timezone });
      toast.success("Tenant timezone updated");
      onChanged();
    } catch (e) { toastError(e); }
    finally { setBusy(false); }
  };

  const provision = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/owners`, {
        name: ownerName, email: ownerEmail,
        phone: ownerPhone || null, allow_additional_owner: allowAdditional,
      });
      setWelcome(data);
      setOwnerName(""); setOwnerEmail(""); setOwnerPhone(""); setAllowAdditional(false);
      onChanged();
    } catch (e) { toastError(e); }
    finally { setBusy(false); }
  };

  const resetLogin = async (userId: string) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/platform/tenants/${tenant.id}/owners/${userId}/temporary-login`
      );
      setWelcome(data);
    } catch (e) { toastError(e); }
    finally { setBusy(false); }
  };

  const copyWelcome = async () => {
    if (!welcome) return;
    await navigator.clipboard.writeText(welcomeText(welcome));
    toast.success("Welcome login copied");
  };

  const downloadWelcome = async () => {
    if (!welcome) return;
    const blob = buildWelcomePdf({
      tenantName: welcome.tenant.name,
      tenantSlug: welcome.tenant.slug,
      ownerName: welcome.owner.name,
      ownerEmail: welcome.owner.email,
      temporaryPassword: welcome.temporary_password,
      loginUrl: `${window.location.origin}/login`,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${welcome.tenant.slug}-welcome-login.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    if (welcome.credential_issuance) await api.post(`/platform/tenants/${welcome.tenant.id}/users/${welcome.owner.id}/login-instructions/${welcome.credential_issuance.id}/downloaded`);
    toast.success("Welcome PDF downloaded and recorded");
  };

  const close = () => {
    if (welcome) setWelcome(null);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`tenant-functions-${tenant.slug}`}>
          <Settings2 className="w-3.5 h-3.5 mr-1" /> Tenant Functions
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl" data-testid="tenant-functions-dialog">
        <DialogHeader><DialogTitle>Tenant Functions — {tenant.name}</DialogTitle></DialogHeader>
        {welcome ? (
          <div className="space-y-4" data-testid="welcome-login-once">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Copy or download this login now. The temporary password will not be shown again after this window closes.
            </div>
            <div className="rounded-lg border bg-slate-50 p-4 font-mono text-sm space-y-2">
              <div><span className="text-slate-500">Tenant:</span> {welcome.tenant.name}</div>
              <div><span className="text-slate-500">Login URL:</span> {window.location.origin}/login</div>
              <div><span className="text-slate-500">Email:</span> {welcome.owner.email}</div>
              <div data-testid="temporary-password"><span className="text-slate-500">Temporary Password:</span> {welcome.temporary_password}</div>
              <div className="text-xs text-slate-500">Password change required on first login.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={downloadWelcome} data-testid="download-welcome-pdf"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
              <Button onClick={copyWelcome}><Copy className="w-4 h-4 mr-1" /> Copy Welcome Login</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            <section>
              <h3 className="font-medium mb-2">Business timezone</h3>
              <div className="flex flex-col sm:flex-row items-start gap-2">
                <div className="w-full">
                  <TimezoneSelect value={timezone} onChange={setTimezone}
                    id={`tenant-timezone-${tenant.id}`} testId="tenant-functions-timezone" />
                </div>
                <Button variant="outline" onClick={saveTimezone}
                  disabled={busy || !timezone || timezone === tenant.default_timezone}
                  data-testid="save-tenant-timezone">Save timezone</Button>
              </div>
            </section>
            <section className="border-t pt-4">
              <h3 className="font-medium mb-2">Tenant users</h3>
              {loading ? <p className="text-sm text-slate-500">Loading…</p> :
                users.length === 0 ? <p className="text-sm text-slate-500">No login users have been provisioned.</p> :
                <div className="border rounded-lg divide-y">
                  {users.map((u) => (
                    <div key={u.id} className="p-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-sm">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email} · {u.role || "No role"}</div>
                      </div>
                      {u.role === "owner_admin" && (
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => resetLogin(u.id)} data-testid={`reset-owner-${u.id}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> New temporary login
                        </Button>
                      )}
                    </div>
                  ))}
                </div>}
            </section>
            <section className="border-t pt-4">
              <h3 className="font-medium mb-3">{users.some((u) => u.role === "owner_admin") ? "Add another Owner" : "Create Initial Owner Login"}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Owner full name</Label><Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} data-testid="owner-name" /></div>
                <div className="space-y-1"><Label>Owner email</Label><Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} data-testid="owner-email" /></div>
                <div className="space-y-1 sm:col-span-2"><Label>Phone (optional)</Label><Input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} /></div>
              </div>
              {users.some((u) => u.role === "owner_admin") && (
                <label className="mt-3 flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={allowAdditional} onChange={(e) => setAllowAdditional(e.target.checked)} className="mt-1" />
                  I intentionally want to add another Tenant Owner.
                </label>
              )}
              <div className="mt-4 flex justify-end">
                <Button onClick={provision}
                  disabled={busy || tenant.status !== "active" || ownerName.trim().length < 2 || !ownerEmail || (users.some((u) => u.role === "owner_admin") && !allowAdditional)}
                  data-testid="provision-owner-submit">
                  {busy ? "Creating…" : "Generate Welcome Login"}
                </Button>
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [tz, setTz] = useState("UTC");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/platform/tenants"); setTenants(data); }
    catch (e) { toastError(e); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/platform/tenants", { name, slug, default_timezone: tz });
      toast.success("Tenant created");
      setOpen(false); setName(""); setSlug(""); setTz("UTC"); load();
    } catch (e) { toastError(e); }
    finally { setBusy(false); }
  };

  const suspend = async (id: string) => {
    try { await api.post(`/platform/tenants/${id}/suspend`); toast.success("Suspended"); load(); }
    catch (e) { toastError(e); }
  };
  const reactivate = async (id: string) => {
    try { await api.post(`/platform/tenants/${id}/reactivate`); toast.success("Reactivated"); load(); }
    catch (e) { toastError(e); }
  };

  return (
    <>
      <PageHeader title="Tenants" subtitle="Platform-wide tenant management" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="create-tenant-btn"><Plus className="w-4 h-4 mr-1" /> Create tenant</Button></DialogTrigger>
          <DialogContent data-testid="create-tenant-dialog">
            <DialogHeader><DialogTitle>New tenant</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tenant-name" /></div>
              <div className="space-y-1"><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="tenant-slug" /></div>
              <div className="space-y-1"><Label htmlFor="tenant-tz">Default timezone</Label><TimezoneSelect value={tz} onChange={setTz} id="tenant-tz" testId="tenant-tz" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={busy || !name || !slug || !tz} data-testid="tenant-create-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {tenants.length === 0 ? <EmptyState icon={Building2} title="No tenants yet" description="Create your first tenant to get started." /> : (
          <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
            <table className="w-full text-sm" data-testid="tenants-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest"><tr>
                <th className="text-left px-4 py-2.5 font-medium">Name</th><th className="text-left px-4 py-2.5 font-medium">Slug</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th><th className="text-left px-4 py-2.5 font-medium">Template</th>
                <th className="text-left px-4 py-2.5 font-medium">Timezone</th><th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr></thead>
              <tbody>{tenants.map((t) => (
                <tr key={t.id} className="border-t border-slate-200 hover:bg-slate-50/60" data-testid={`tenant-row-${t.slug}`}>
                  <td className="px-4 py-2.5 font-medium"><Link to={`/platform/tenants/${t.id}`} className="hover:underline">{t.name}</Link></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{t.slug}</td>
                  <td className="px-4 py-2.5"><span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${t.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>{t.status}</span></td>
                  <td className="px-4 py-2.5 text-slate-600">{t.applied_template || "—"} <span className="text-xs text-slate-400">{t.applied_template_version}</span></td>
                  <td className="px-4 py-2.5 text-slate-600">{t.default_timezone}</td>
                  <td className="px-4 py-2.5"><div className="flex justify-end items-center gap-2">
                    <Link to={`/platform/tenants/${t.id}`}><Button variant="outline" size="sm" data-testid={`manage-${t.slug}`}>Manage</Button></Link>
                    <TenantFunctions tenant={t} onChanged={load} />
                    {t.status === "active" ? (
                      <button className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" onClick={() => suspend(t.id)} data-testid={`suspend-${t.slug}`}><Ban className="w-3.5 h-3.5" /> Suspend</button>
                    ) : (
                      <button className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1" onClick={() => reactivate(t.id)} data-testid={`reactivate-${t.slug}`}><Play className="w-3.5 h-3.5" /> Reactivate</button>
                    )}
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
